import type { ApiClient } from './client';
import type { AdminEventSummary } from '../../types/event';

export const adminMethods = {
    // Admin Analytics
    async getAdminAnalytics(timeframe = '30d'): Promise<any> {
      const response = await this.makeRequest<any>(
        `${this.getAdminEndpoint('/admin/analytics.php')}?timeframe=${encodeURIComponent(timeframe)}`
      );

      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch admin analytics');
      }

      return response.data;
    },

    // Admin User Management
    async getAdminUsers(params?: {
      page?: number;
      limit?: number;
      search?: string;
      role_filter?: string;
      sort_by?: string;
      sort_order?: 'asc' | 'desc';
    }): Promise<any> {
      const queryParams = new URLSearchParams();

      if (params) {
        const paramMap: Record<string, string | number | undefined> = {
          page: params.page,
          limit: params.limit,
          search: params.search,
          role: params.role_filter,
          sort: params.sort_by,
          order: params.sort_order,
        };
        Object.entries(paramMap).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            queryParams.append(key, value.toString());
          }
        });
      }

      const endpoint = `${this.getAdminEndpoint('/admin/users.php')}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await this.makeRequest<any>(endpoint);

      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch users');
      }

      // Return the complete response structure (data, pagination, statistics)
      return {
        data: response.data,
        pagination: (response as any).pagination
          ? {
              ...(response as any).pagination,
              total_pages: (response as any).pagination.total_pages ?? (response as any).pagination.pages,
            }
          : undefined,
        statistics: (response as any).statistics
      };
    },


    async updateUser(
      userId: number,
      updates: {
        role?: 'user' | 'super_admin';
        can_organize?: boolean;
        can_judge?: boolean;
        is_active?: boolean;
      }
    ): Promise<any> {
      const response = await this.makeRequest<any>(this.getAdminEndpoint('/admin/users.php'), {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          id: userId,
          ...updates,
        })
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to update user');
      }

      return response.data;
    },

    async updateUserRole(userId: number, role: 'user' | 'super_admin'): Promise<any> {
      const response = await this.makeRequest<any>(this.getAdminEndpoint('/admin/users.php'), {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          id: userId,
          role,
        })
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to update user role');
      }

      return response.data;
    },


    async deleteUser(userId: number): Promise<any> {
      const response = await this.makeRequest<any>(`${this.getAdminEndpoint('/admin/users.php')}?id=${userId}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to delete user');
      }

      return response.data;
    },

    // Admin Registration Codes
    async getRegistrationCodes(params?: {
      page?: number;
      limit?: number;
      search?: string;
      status_filter?: 'all' | 'active' | 'used' | 'expired';
      sort_by?: string;
      sort_order?: 'asc' | 'desc';
    }): Promise<any> {
      const queryParams = new URLSearchParams();

      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            queryParams.append(key, value.toString());
          }
        });
      }

      const endpoint = `${this.getAdminEndpoint('/admin/registration_codes.php')}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await this.makeRequest<any>(endpoint);

      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch registration codes');
      }

      // Return the complete response structure (data, pagination)
      return {
        data: response.data,
        pagination: (response as any).pagination,
        statistics: (response as any).statistics || null
      };
    },


    async createRegistrationCode(codeData: {
      description?: string;
      expires_at?: string;
      expires_days?: number;
      grants_can_organize?: boolean;
      grants_can_judge?: boolean;
      max_uses?: number;
    }): Promise<any> {
      const response = await this.makeRequest<any>(this.getAdminEndpoint('/admin/registration_codes.php'), {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(codeData)
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to create registration code');
      }

      return response.data;
    },


    async updateRegistrationCode(codeId: number, updates: {
      description?: string;
      is_active?: boolean;
      expires_at?: string;
      expires_days?: number;
      grants_can_organize?: boolean;
      grants_can_judge?: boolean;
      max_uses?: number;
    }): Promise<any> {
      const response = await this.makeRequest<any>(this.getAdminEndpoint('/admin/registration_codes.php'), {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          id: codeId,
          ...updates
        })
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to update registration code');
      }

      return response.data;
    },


    async deleteRegistrationCode(codeId: number): Promise<any> {
      const response = await this.makeRequest<any>(`${this.getAdminEndpoint('/admin/registration_codes.php')}?id=${codeId}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to delete registration code');
      }

      return response.data;
    },

    // Admin AI / LLM settings
    async getAdminAiSettings(): Promise<any> {
      const response = await this.makeRequest<any>(this.getAdminEndpoint('/admin/ai_settings.php'), {
        headers: this.getAuthHeaders(),
      });

      if (!response.success) {
        throw new Error(response.error || response.message || 'Failed to fetch AI settings');
      }

      return response.data;
    },

    async updateAdminAiSettings(settings: Record<string, unknown>): Promise<any> {
      const response = await this.makeRequest<any>(this.getAdminEndpoint('/admin/ai_settings.php'), {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ settings }),
      });

      if (!response.success) {
        throw new Error(response.error || response.message || 'Failed to update AI settings');
      }

      return response.data;
    },

    async testAdminAiConnection(): Promise<any> {
      const response = await this.makeRequest<any>(this.getAdminEndpoint('/admin/ai_settings.php'), {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ action: 'test' }),
      });

      if (!response.success) {
        throw new Error(response.error || response.message || 'AI connection test failed');
      }

      return response.data;
    },

    async seedDemoShowcase(options?: {
      action?: 'seed' | 'clear';
      force?: boolean;
      dry_run?: boolean;
      include_event?: boolean;
      include_clubs?: boolean;
      include_shops?: boolean;
    }): Promise<{
      password?: string;
      organizer?: string;
      judge?: string;
      event_slug?: string | null;
      vehicles_upserted?: number;
      vehicles_created?: number;
      vehicles?: number;
      users?: number;
      modifications?: number;
      images?: number;
      awards?: number;
      badge_definitions?: number;
      badges_issued?: number;
      favorites?: number;
      passport_encounters?: number;
      discover_hint?: string;
      clubs?: Array<{
        slug: string;
        name: string;
        verification_status: string;
        path: string;
      }>;
      shops?: Array<{
        slug: string;
        name: string;
        directory_status: string;
        path: string;
      }>;
      builds?: Array<{
        tag: string;
        username: string;
        slug: string;
        path: string;
        mods: number;
        images: number;
        public: boolean;
      }>;
      message?: string;
    }> {
      const response = await this.makeRequest<any>(this.getAdminEndpoint('/admin/seed_demo.php'), {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          action: options?.action ?? 'seed',
          force: options?.force ?? false,
          dry_run: options?.dry_run ?? false,
          include_event: options?.include_event ?? true,
          include_clubs: options?.include_clubs ?? true,
          include_shops: options?.include_shops ?? true,
        }),
      });

      if (!response.success) {
        throw new Error(response.error || response.message || 'Failed to update demo data');
      }

      return {
        ...(response.data || {}),
        message: response.message,
      };
    },

    async clearAdminAiSecret(key: string): Promise<any> {
      const response = await this.makeRequest<any>(
        `${this.getAdminEndpoint('/admin/ai_settings.php')}?key=${encodeURIComponent(key)}`,
        {
          method: 'DELETE',
          headers: this.getAuthHeaders(),
        }
      );

      if (!response.success) {
        throw new Error(response.error || response.message || 'Failed to clear secret override');
      }

      return response.data;
    },

    // Public Registration Code Validation
    async validateRegistrationCode(code: string): Promise<any> {
      // Auth endpoints also need the same treatment
      const authEndpoint = '/auth/validate_code.php';
      const response = await this.makeRequest<any>(authEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code })
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to validate registration code');
      }

      // Return the full response structure, not just data
      return {
        valid: (response as any).valid,
        code: response.data,
        message: response.message
      };
    },

    async getAdminEvents(): Promise<AdminEventSummary[]> {
      const response = await this.makeRequest<any>(this.getAdminEndpoint('/admin/events.php'));
      if (response.success && Array.isArray(response.data)) return response.data;
      throw new Error(response.message || response.error || 'Failed to load events');
    },

    /**
     * Super-admin hard-delete with cascade/detach.
     * `confirm` must match the entity slug, name, username, or vehicle public_slug.
     */
    async adminDeleteEntity(
      entity: 'event' | 'club' | 'shop' | 'vehicle' | 'user',
      id: number,
      confirm: string
    ): Promise<{ deleted: boolean; summary: Record<string, unknown>; label?: string }> {
      const response = await this.makeRequest<any>(this.getAdminEndpoint('/admin/entity_delete.php'), {
        method: 'POST',
        headers: {
          ...this.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ entity, id, confirm }),
      });

      if (!response.success) {
        throw new Error(
          (response as { error?: string }).error ||
            response.message ||
            `Failed to delete ${entity}`
        );
      }

      const payload = response as {
        deleted?: boolean;
        summary?: Record<string, unknown>;
        label?: string;
        data?: { summary?: Record<string, unknown>; label?: string };
      };

      return {
        deleted: Boolean(payload.deleted ?? true),
        summary: payload.summary || payload.data?.summary || {},
        label: payload.label || payload.data?.label,
      };
    },
} satisfies ThisType<ApiClient>;
